import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { ArrowLeft, Loader2, Sparkles, Share2, RefreshCw, Lightbulb } from "lucide-react";

export default function WhatIf() {
  const { http } = useAuth();
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    http.get("/whatif").then(({ data }) => setScenarios(data)).catch(() => {}).finally(() => setLoading(false));
  }, [http]);

  const open = (s) => { setActive(s); setAnswer(null); };

  const pick = async (i) => {
    if (submitting || answer) return;
    setSubmitting(true);
    try {
      const { data } = await http.post("/whatif/answer", { scenario_id: active.id, choice: i });
      setAnswer({ ...data, choice: i });
    } catch { toast.error("Não foi possível carregar a consequência."); }
    finally { setSubmitting(false); }
  };

  const share = async () => {
    const text = `EU ESCOLHI: "${answer.escolha}"\n${active.titulo}\nE SE FOSSE COMIGO? — e você, o que faria?`;
    if (navigator.share) {
      try { await navigator.share({ title: "E Se Fosse Comigo?", text, url: window.location.origin }); return; }
      catch (err) { console.debug("share cancelado", err); }
    }
    try { await navigator.clipboard.writeText(`${text}\n${window.location.origin}`); toast.success("Decisão copiada!"); }
    catch (err) { console.error("clipboard falhou", err); toast.error("Não foi possível compartilhar."); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-2xl mx-auto px-5 py-8">
        {!active ? (
          <>
            <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Voltar
            </button>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FACC15]/15 border border-[#FACC15]/30 text-[#FACC15] text-xs font-semibold uppercase tracking-widest mb-4">
              <Sparkles className="w-4 h-4" /> E SE...?
            </div>
            <h1 className="font-display font-black text-4xl tracking-tighter">Cenários inesperados</h1>
            <p className="text-slate-400 mt-2">Escolha um "e se" e descubra a consequência. Sem certo ou errado — só reflexão.</p>

            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F43F5E]" /></div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 mt-8">
                {scenarios.map((s, i) => (
                  <motion.button key={s.id} data-testid={`whatif-${s.id}`}
                    initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    onClick={() => open(s)}
                    className="group text-left rounded-3xl p-6 bg-[#0F0F14] border border-white/10 hover:border-[#FACC15]/40 hover:-translate-y-1 transition-all">
                    <span className="text-3xl">{s.emoji}</span>
                    <p className="font-display font-black text-lg tracking-tight mt-3 leading-tight">{s.titulo}</p>
                  </motion.button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button onClick={() => setActive(null)} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Outros cenários
            </button>
            <div className="text-4xl mb-3">{active.emoji}</div>
            <h1 className="font-display font-black text-3xl tracking-tighter">{active.titulo}</h1>
            <div className="rounded-3xl p-6 bg-[#0F0F14] border border-white/10 mt-5">
              <p className="font-display text-xl leading-snug">{active.narrativa}</p>
            </div>

            {!answer ? (
              <div className="space-y-3 mt-5">
                {active.opcoes.map((o, i) => (
                  <button key={o.texto} data-testid={`whatif-option-${i}`} onClick={() => pick(i)} disabled={submitting}
                    className="group w-full text-left rounded-2xl p-5 bg-[#0F0F14] border border-white/10 hover:border-[#FACC15]/60 active:scale-[0.98] transition-all disabled:opacity-60">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 w-6 h-6 shrink-0 grid place-items-center rounded-full bg-white/5 border border-white/10 text-xs font-bold text-slate-400 group-hover:bg-[#FACC15] group-hover:text-black transition-colors">
                        {String.fromCharCode(65 + i)}
                      </span>
                      <span className="text-[15px] leading-snug">{o.texto}</span>
                    </div>
                  </button>
                ))}
                {submitting && <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-[#FACC15]" /></div>}
              </div>
            ) : (
              <AnimatePresence>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="whatif-result" className="mt-5 space-y-4">
                  <div className="rounded-3xl p-6 bg-gradient-to-br from-[#FACC15]/15 to-[#F43F5E]/10 border border-[#FACC15]/25">
                    <p className="text-sm text-slate-400">Você escolheu: <span className="text-white">{answer.escolha}</span></p>
                    <p className="font-display text-lg leading-snug mt-2">{answer.consequencia}</p>
                  </div>
                  <div className="rounded-2xl p-4 bg-white/5 border border-white/10 flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-[#06B6D4] shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-300">{answer.explicacao}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <button data-testid="whatif-share" onClick={share}
                      className="flex items-center justify-center gap-2 rounded-2xl py-3.5 bg-[#F43F5E] hover:bg-[#E11D48] active:scale-95 transition-all font-semibold text-white">
                      <Share2 className="w-4 h-4" /> Compartilhar
                    </button>
                    <button onClick={() => setAnswer(null)}
                      className="flex items-center justify-center gap-2 rounded-2xl py-3.5 bg-white/5 border border-white/10 hover:bg-white/10 active:scale-95 transition-all font-semibold">
                      <RefreshCw className="w-4 h-4" /> E se eu mudasse?
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
            <p className="text-[11px] text-slate-500 mt-6 text-center">Ficção para entretenimento e reflexão. Não é aconselhamento.</p>
          </>
        )}
      </div>
    </div>
  );
}
